import React from "react";
import { Calendar, Card } from "@lemonadetraining/web-ui";
import { addGymDays, gymWeekStart, keyToLocalDate, localDateToKey } from "../lib/gymClock";

// Days are gym-calendar keys; the Calendar only ever sees browser-local tokens of them.
interface StaticCalWeekPickerProps {
  todayKey: string;
  selectedKey: string;
  onDateChange?: (selectedKey: string) => void;
  minKey?: string | null;
  maxKey?: string | null;
}

const StaticCalWeekPicker: React.FC<StaticCalWeekPickerProps> = (props) => {
  const startKey = gymWeekStart(props.selectedKey);
  const selectedWeek = {
    from: keyToLocalDate(startKey),
    to: keyToLocalDate(addGymDays(startKey, 6)),
  };

  const handleDayClick = (day: Date) => props.onDateChange?.(localDateToKey(day));

  return (
    <Card className="border-none shadow-none bg-transparent px-4 py-2">
      <Calendar
        className="p-0"
        showWeekNumber
        showOutsideDays
        today={keyToLocalDate(props.todayKey)}
        defaultMonth={keyToLocalDate(props.selectedKey)}
        onDayClick={handleDayClick}
        classNames={{
          month: "flex flex-col gap-3",
          months: "relative flex flex-col",
          month_caption: "flex justify-center pt-1 relative items-center w-full",
          button_previous:
            "absolute left-1 top-1 z-10 inline-flex items-center justify-center size-7 border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md opacity-50 hover:opacity-100",
          button_next:
            "absolute right-1 top-1 z-10 inline-flex items-center justify-center size-7 border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md opacity-50 hover:opacity-100",
          week: "flex w-full mt-0.5",
          day: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-primary/15 [&:has([aria-selected].day-outside)]:bg-primary/10 [&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md",
          today: "ring-2 ring-primary rounded-md",
          selected: "bg-primary/15 text-foreground",
          range_middle: "aria-selected:bg-primary/15 aria-selected:text-foreground",
          range_start: "day-range-start rounded-l-md",
          range_end: "day-range-end rounded-r-md",
          outside:
            "day-outside text-muted-foreground opacity-50 aria-selected:bg-primary/10 aria-selected:text-muted-foreground aria-selected:opacity-60",
        }}
        modifiers={{
          selected: selectedWeek,
          range_start: [selectedWeek.from],
          range_end: [selectedWeek.to],
          range_middle: selectedWeek,
          pickedDay: [keyToLocalDate(props.selectedKey)],
        }}
        modifiersClassNames={{
          pickedDay:
            "[&>button]:bg-foreground [&>button]:text-background [&>button]:hover:bg-foreground [&>button]:hover:text-background rounded-md",
        }}
        disabled={[
          ...(props.minKey ? [{ before: keyToLocalDate(props.minKey) }] : []),
          ...(props.maxKey ? [{ after: keyToLocalDate(props.maxKey) }] : []),
        ]}
      />
    </Card>
  );
};

export default StaticCalWeekPicker;
